import React, { useState, useMemo } from "react";

import { RoleTypeExtension } from "../../constants";
import { pad5 } from "../../libs";
import type { GeneralUserData } from "../../types/generalUserManage";
import { formatLastActive } from "../../utils/generalUserManage";
import { NoticeRoleIcon } from "../SVGIcons";

import GeneralUserListActionBar from "./GeneralUserListActionBar";
import Pagination from "./Pagination";
import SortableTableHeader from "./SortableTableHeader";

const GeneralUserSortField = {
  EMPLOYEE_ID: "employee_id",
  FULL_NAME: "full_name",
  USER_NAME: "user_name",
  ROLE_TYPE: "role_type",
  EMAIL: "email",
  CREDIT_CONSUMED: "credit_consumed",
  CREDIT_LIMIT: "credit_limit",
  LAST_ACTIVE_TIME: "last_active_time",
} as const;

const SortOrder = {
  NONE: "none",
  ASC: "asc",
  DESC: "desc",
} as const;

type SortField =
  (typeof GeneralUserSortField)[keyof typeof GeneralUserSortField];
type SortOrderType = (typeof SortOrder)[keyof typeof SortOrder];

interface GeneralUserListSort {
  field: SortField | null;
  order: SortOrderType;
}

interface GeneralUserListProps {
  users: GeneralUserData[];
  periodMonthText: string;
  currentUserId?: number;
  isViewer?: boolean;
  onBulkCreateUser: () => void;
  onCreateUser: () => void;
  onEditUser: (user: GeneralUserData) => void; // eslint-disable-line no-unused-vars
  onViewUser: (user: GeneralUserData) => void; // eslint-disable-line no-unused-vars
  onDeleteUser: (user: GeneralUserData) => void; // eslint-disable-line no-unused-vars
  onLimitSetting: () => void;
}

const GeneralUserList: React.FC<GeneralUserListProps> = ({
  users,
  periodMonthText,
  currentUserId,
  isViewer = false,
  onBulkCreateUser,
  onCreateUser,
  onEditUser,
  onViewUser,
  onDeleteUser,
  onLimitSetting,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [sort, setSort] = useState<GeneralUserListSort>({
    field: null,
    order: SortOrder.NONE,
  });
  const [searchKeyword, setSearchKeyword] = useState("");
  const itemsPerPage = 10; // 1ページあたりの表示件数

  const handleSortChange = (field: SortField) => {
    setSort((prevSort) => {
      // 同じフィールドの場合は order を切り替え
      if (prevSort.field === field) {
        if (prevSort.order === SortOrder.NONE) {
          return { field, order: SortOrder.ASC };
        }
        if (prevSort.order === SortOrder.ASC) {
          return { field, order: SortOrder.DESC };
        }
        return { field: null, order: SortOrder.NONE };
      }
      // 別のフィールドの場合は新しいフィールドで asc からスタート
      return { field, order: SortOrder.ASC };
    });
  };

  // 検索フィルタリングとソート済みユーザーリストを計算
  const sortedUsers = useMemo(() => {
    // まず検索キーワードでフィルタリング
    let filterUsers = users;
    if (searchKeyword.trim()) {
      const keyword = searchKeyword.toLowerCase();
      filterUsers = users.filter(
        (user) =>
          (user.full_name && user.full_name.toLowerCase().includes(keyword)) ||
          (user.user_name && user.user_name.toLowerCase().includes(keyword)) ||
          (user.email && user.email.toLowerCase().includes(keyword)),
      );
    }

    // ソートが指定されていない場合はフィルタリング結果をそのまま返す
    if (!sort.field || sort.order === SortOrder.NONE) {
      return filterUsers;
    }

    const sorted = [...filterUsers];
    const { field, order } = sort;

    sorted.sort((a, b) => {
      let aVal: any; // eslint-disable-line @typescript-eslint/no-explicit-any
      let bVal: any; // eslint-disable-line @typescript-eslint/no-explicit-any

      switch (field) {
        case GeneralUserSortField.EMPLOYEE_ID:
          aVal = a.employee_id || 0;
          bVal = b.employee_id || 0;
          break;
        case GeneralUserSortField.FULL_NAME:
          aVal = a.full_name || "";
          bVal = b.full_name || "";
          break;
        case GeneralUserSortField.USER_NAME:
          aVal = a.user_name || "";
          bVal = b.user_name || "";
          break;
        case GeneralUserSortField.ROLE_TYPE:
          aVal = a.role_type || "";
          bVal = b.role_type || "";
          break;
        case GeneralUserSortField.EMAIL:
          aVal = a.email || "";
          bVal = b.email || "";
          break;
        case GeneralUserSortField.CREDIT_CONSUMED:
          aVal = a.credit_consumed || 0;
          bVal = b.credit_consumed || 0;
          break;
        case GeneralUserSortField.CREDIT_LIMIT:
          aVal =
            a.credit_limit === null ? Number.MAX_SAFE_INTEGER : a.credit_limit;
          bVal =
            b.credit_limit === null ? Number.MAX_SAFE_INTEGER : b.credit_limit;
          break;
        case GeneralUserSortField.LAST_ACTIVE_TIME:
          // 両方とも値がない場合
          if (!a.last_active_time && !b.last_active_time) {
            return 0;
          }
          // aのみ値がない場合、aを後ろに配置（昇順/降順を考慮）
          if (!a.last_active_time) {
            return order === SortOrder.ASC ? 1 : -1;
          }
          // bのみ値がない場合、bを後ろに配置（昇順/降順を考慮）
          if (!b.last_active_time) {
            return order === SortOrder.ASC ? -1 : 1;
          }
          aVal = new Date(a.last_active_time).getTime();
          bVal = new Date(b.last_active_time).getTime();
          break;
        default:
          return 0;
      }

      // 文字列の場合はlocaleCompare、数値の場合は減算
      if (typeof aVal === "string" && typeof bVal === "string") {
        return order === SortOrder.ASC
          ? aVal.localeCompare(bVal, "ja")
          : bVal.localeCompare(aVal, "ja");
      }
      return order === SortOrder.ASC ? aVal - bVal : bVal - aVal;
    });

    return sorted;
  }, [users, sort, searchKeyword]);

  const pageCount = Math.max(1, Math.ceil(sortedUsers.length / itemsPerPage));

  // 現在のページに表示するユーザーのリストを計算
  const displayedUsers = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return sortedUsers.slice(startIndex, endIndex);
  }, [sortedUsers, currentPage]);

  const handleSearch = (keyword: string) => {
    setSearchKeyword(keyword);
    setCurrentPage(1); // 検索時は1ページ目に戻す
  };

  const handleClearSearch = () => {
    setSearchKeyword("");
    setCurrentPage(1);
  };

  return (
    <>
      <style>{`
        .orange-tooltip .p-tooltip-text {
          background-color: rgb(249 115 22);
          color: white;
          white-space: pre-line;
        }
        .orange-tooltip .p-tooltip-arrow {
          border-right-color: rgb(20 115 22);
        }
      `}</style>
      <div className="flex flex-col h-full p-4">
        <div className="bg-white rounded-lg shadow flex-1 flex flex-col overflow-hidden p-6 m-4">
          <div className="mb-4">
            <h2 className="text-2xl font-bold text-gray-900">
              ユーザー管理（{sortedUsers.length}人）
            </h2>
          </div>

          {/* アクションバー */}
          <div className="mb-4">
            <GeneralUserListActionBar
              onSearch={handleSearch}
              onClearSearch={handleClearSearch}
              onLimitSetting={onLimitSetting}
              onBulkCreateUser={onBulkCreateUser}
              onCreateUser={onCreateUser}
              isViewer={isViewer}
            />
          </div>

          <div className="flex-1 overflow-x-auto overflow-y-auto">
            <table className="min-w-full border-collapse border-gray-300">
              <thead className="bg-[#5571ad]">
                <tr>
                  <SortableTableHeader
                    label="No"
                    field={GeneralUserSortField.EMPLOYEE_ID}
                    currentField={sort.field}
                    currentOrder={sort.order}
                    onSort={handleSortChange}
                  />
                  <SortableTableHeader
                    label="ユーザー名"
                    field={GeneralUserSortField.FULL_NAME}
                    currentField={sort.field}
                    currentOrder={sort.order}
                    onSort={handleSortChange}
                  />
                  <SortableTableHeader
                    label="ログインID"
                    field={GeneralUserSortField.USER_NAME}
                    currentField={sort.field}
                    currentOrder={sort.order}
                    onSort={handleSortChange}
                  />
                  <SortableTableHeader
                    label="権限"
                    field={GeneralUserSortField.ROLE_TYPE}
                    currentField={sort.field}
                    currentOrder={sort.order}
                    onSort={handleSortChange}
                    icon={<NoticeRoleIcon />}
                  />
                  <SortableTableHeader
                    label="メールアドレス"
                    field={GeneralUserSortField.EMAIL}
                    currentField={sort.field}
                    currentOrder={sort.order}
                    onSort={handleSortChange}
                  />
                  <SortableTableHeader
                    label={`使用量(${periodMonthText})`}
                    field={GeneralUserSortField.CREDIT_CONSUMED}
                    currentField={sort.field}
                    currentOrder={sort.order}
                    onSort={handleSortChange}
                  />
                  <SortableTableHeader
                    label={`個別上限(${periodMonthText})`}
                    field={GeneralUserSortField.CREDIT_LIMIT}
                    currentField={sort.field}
                    currentOrder={sort.order}
                    onSort={handleSortChange}
                  />
                  <SortableTableHeader
                    label="最終更新時刻"
                    field={GeneralUserSortField.LAST_ACTIVE_TIME}
                    currentField={sort.field}
                    currentOrder={sort.order}
                    onSort={handleSortChange}
                  />
                  <th
                    className="sticky top-0 px-3 py-2 text-center text-white bg-[#5571ad] border border-[#889bc5] whitespace-nowrap"
                    style={{ right: "-1px" }}
                  >
                    管理
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white text-sm">
                {displayedUsers.map((user, index) => (
                  <tr
                    key={user.employee_id || index}
                    className="hover:bg-sky-50"
                  >
                    <td className="p-2 border border-gray-300 text-center">
                      {user.employee_id ? pad5(user.employee_id) : "-"}
                    </td>
                    <td className="p-2 border border-gray-300">
                      {user.full_name || "-"}
                    </td>
                    <td className="p-2 border border-gray-300">
                      {user.user_name || "-"}
                    </td>
                    <td className="p-2 border border-gray-300 text-center">
                      {RoleTypeExtension.isAdmin(user.role_type) ? (
                        <span className="px-2 py-1 text-xs text-white bg-orange-500 rounded-full">
                          {RoleTypeExtension.getLabel(user.role_type)}
                        </span>
                      ) : RoleTypeExtension.isViewer(user.role_type) ? (
                        <span className="px-2 py-1 text-xs text-white bg-blue-600 rounded-full">
                          {RoleTypeExtension.getLabel(user.role_type)}
                        </span>
                      ) : user.role_type ? (
                        RoleTypeExtension.getLabel(user.role_type)
                      ) : (
                        "-"
                      )}
                    </td>
                    <td className="p-2 border border-gray-300">
                      {user.email || "-"}
                    </td>
                    <td className="p-2 border border-gray-300 text-center">
                      {user.credit_consumed || 0}
                    </td>
                    <td className="p-2 border border-gray-300 text-center">
                      {user.credit_limit !== null
                        ? user.credit_limit
                        : "上限なし"}
                    </td>
                    <td className="p-2 border border-gray-300 text-center">
                      {formatLastActive(user.last_active_time)}
                    </td>
                    <td
                      className="sticky bg-white p-2 border border-gray-300"
                      style={{ right: "-1px" }}
                    >
                      <div className="flex items-center justify-center space-x-2">
                        <button
                          type="button"
                          onClick={() => onViewUser(user)}
                          className="px-2 py-1 text-xs font-medium rounded border border-blue-700 text-white bg-blue-700 hover:bg-blue-600 whitespace-nowrap"
                        >
                          詳細
                        </button>
                        <button
                          type="button"
                          onClick={() => onEditUser(user)}
                          disabled={isViewer}
                          className={`px-2 py-1 text-xs font-medium rounded border whitespace-nowrap ${
                            isViewer
                              ? "border-gray-300 text-gray-400 bg-gray-100 cursor-not-allowed"
                              : "border-blue-700 text-blue-700 bg-white hover:text-white hover:bg-blue-700"
                          }`}
                        >
                          編集
                        </button>
                        <button
                          type="button"
                          onClick={() => onDeleteUser(user)}
                          disabled={isViewer || currentUserId === user.user_id}
                          className={`px-2 py-1 text-xs font-medium rounded border whitespace-nowrap ${
                            isViewer || currentUserId === user.user_id
                              ? "border-gray-300 text-gray-400 bg-gray-100 cursor-not-allowed"
                              : "border-red-700 text-red-600 bg-white hover:text-white hover:bg-red-700"
                          }`}
                        >
                          削除
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ページネーション（下部に配置） */}
          <div className="mt-4">
            <Pagination
              page={currentPage}
              pageCount={pageCount}
              onPageChange={setCurrentPage}
            />
            <div className="text-center mt-2">
              <span className="text-sm text-gray-600 font-bold">
                ー {currentPage} / {pageCount} ー
              </span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default GeneralUserList;
